import { useMemo } from "react";
import { useOptionalAppConfig } from "../../services/studio/AppConfigProvider";
import {
  CONNECTOR_PANELS,
  normaliseSystemName,
} from "../../integrations/connectorRegistry";
import type { StudioModule, StudioModuleConnector } from "../../services/studio/types";

const EMPTY: readonly StudioModuleConnector[] = Object.freeze([]);

function normalize(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "");
}

/**
 * Resolves the connectors[] array for a module addressed by its tab id
 * (e.g. "engage", "deckorate"). Matching order:
 *   1. lowercased module name (e.g. "MyQueue" tab → backend module "MyQueue")
 *   2. any connector's systemName (legacy tab ids that mirror the connector)
 *   3. panel-equivalence — the tab id maps to a panel via CONNECTOR_PANELS
 *      and a backend module has a connector whose systemName also maps to
 *      that same panel (lets the static "engage" tab open the backend
 *      "Workflow" module's engage-workflow connectors).
 *
 * Returns an empty list when Studio settings aren't loaded yet — callers
 * should treat this as "fall back to single-connector legacy rendering".
 */
export function useModuleConnectors(moduleId: string): readonly StudioModuleConnector[] {
  const config = useOptionalAppConfig();
  const modules = config?.data?.modules;

  return useMemo(() => {
    if (!modules || modules.length === 0) return EMPTY;
    const target = normalize(moduleId);
    const byName = modules.find((m: StudioModule) => normalize(m.name) === target);
    if (byName) return byName.connectors;
    const bySystem = modules.find((m: StudioModule) =>
      m.connectors.some((c) => normalize(c.systemName) === target),
    );
    if (bySystem) return bySystem.connectors;

    const targetPanel = CONNECTOR_PANELS[moduleId.toLowerCase()];
    if (!targetPanel) return EMPTY;
    const byPanel = modules.find((m: StudioModule) =>
      m.connectors.some(
        (c) => CONNECTOR_PANELS[normaliseSystemName(c.systemName)] === targetPanel,
      ),
    );
    return byPanel?.connectors ?? EMPTY;
  }, [modules, moduleId]);
}
